import type { DigitalCompetency, Partner } from "@expert/shared-types";
import type { AbstractAnalyticsDispatcher } from "@soluto-private/eventualize";
import type { Address } from "@/shared-types/address";

export interface Customer {
    fullName?: string;
    firstName?: string;
    lastName?: string;
    productName?: string;
    purchaseDate?: Date | string;
    accountNumber?: string;
    planId?: string;
    mdn?: string;
    email?: string;
    digitalCompetency?: DigitalCompetency;
    address?: Address;
    intent?: Intent;
    orderId?: string;
    currentSubscriptions?: string[];
    //#region McAfee specific properties
    metadata?: {
        product?: Product;
        feature?: Feature;
        caseId?: string;
        caseNumber?: string;
        isInternationalCaller?: boolean;
    };
    //#endregion
}

export interface DeviceReplacement {
    imei: string;
    make: string;
    model: string;
    iccid?: string;
    eSim: boolean;
}

export interface SessionSummary {
    summary: string;
    timestamp: string;
    resolutionStatus: string;
}

export interface CustomerDetailsPayload {
    primaryAccountHolder?: Customer;
    authorizedUsers?: Customer[];
    previousSummary?: SessionSummary;
    //TODO:// rename in API:
    // 1. BE => add deviceReplacement (same value as deviceActivation)
    // 2. BE => deploy to Prod
    // 3. FE => rename deviceActivation to deviceReplacement
    // 4. FE => deploy to Prod
    // 5. BE => remove deviceActivation
    // 6. BE => deploy to Prod
    deviceActivation?: DeviceReplacement;
    previousDevices?: string[];
    clientChannelId?: string;
}

// just mcafee below this

export const products = [
    "Anti-Tracker",
    "Business Protection Console",
    "Chrome Book",
    "Mac",
    "McAfee Protection Center",
    "Mobile",
    "My Account",
    "Password Manager",
    "PC Optimizer",
    "Security Scan Plus",
    "SHP/MHP",
    "Web Advisor",
    "WPS",
    "WSS",
] as const;
export type Product = (typeof products)[number];

export const features = [
    "Account & Payment Protection",
    "Alerting",
    "Anti-Phishing",
    "Anti-Tracker",
    "Antivirus",
    "App Boost",
    "Business Protection Score",
    "Concierge",
    "Credit Monitoring",
    "Deepfake Detector",
    "Disk Encryption",
    "Email Scam",
    "File Boost",
    "Financial Transaction Monitoring",
    "Firewall",
    "Identity Monitoring",
    "Individual Protection Score",
    "Installer/downloader",
    "Login",
    "Network Boost",
    "Online Account Cleanup",
    "Parental Control",
    "Password Manager",
    "Personal Data Cleanup",
    "Policy Management",
    "Product UI",
    "Protection Setup",
    "Quarantine",
    "Secure Search",
    "Shopping Scam Detector",
    "Shredder",
    "Social Media Protection",
    "Social Privacy Manager",
    "Text Scam Detector",
    "Tracker Remover",
    "User-managed protection",
    "VPN",
    "Web Boost",
    "Wi-Fi",
    "Quick Check",
    "Scam Sensitivity Dial",
    "Text Scam",
    "Video Scam",
] as const;

export type Feature = (typeof features)[number];

export const productMappings: Record<Product, Feature[]> = {
    "Anti-Tracker": ["Login", "Policy Management", "Product UI"],
    "Business Protection Console": [
        "Alerting",
        "Business Protection Score",
        "Identity Monitoring",
        "Individual Protection Score",
        "Login",
        "Policy Management",
        "Product UI",
        "User-managed protection",
    ],
    "Chrome Book": [
        "Alerting",
        "Anti-Phishing",
        "Antivirus",
        "Business Protection Score",
        "Credit Monitoring",
        "Financial Transaction Monitoring",
        "Identity Monitoring",
        "Individual Protection Score",
        "Installer/downloader",
        "Login",
        "Online Account Cleanup",
        "Parental Control",
        "Personal Data Cleanup",
        "Product UI",
        "Quarantine",
        "Social Privacy Manager",
        "VPN",
        "Wi-Fi",
    ],
    Mac: ["Anti-Phishing", "Antivirus", "Firewall", "Installer/downloader", "Product UI", "VPN", "Wi-Fi"],
    "McAfee Protection Center": [
        "Account & Payment Protection",
        "Anti-Tracker",
        "Credit Monitoring",
        "Email Scam",
        "Financial Transaction Monitoring",
        "Identity Monitoring",
        "Individual Protection Score",
        "Installer/downloader",
        "Login",
        "Online Account Cleanup",
        "Password Manager",
        "Personal Data Cleanup",
        "Product UI",
        "Quick Check",
        "Scam Sensitivity Dial",
        "Shopping Scam Detector",
        "Social Privacy Manager",
        "Text Scam",
        "Video Scam",
    ],
    Mobile: [
        "Alerting",
        "Anti-Phishing",
        "Antivirus",
        "Business Protection Score",
        "Credit Monitoring",
        "Email Scam",
        "Financial Transaction Monitoring",
        "Identity Monitoring",
        "Individual Protection Score",
        "Installer/downloader",
        "Login",
        "Online Account Cleanup",
        "Parental Control",
        "Personal Data Cleanup",
        "Product UI",
        "Quarantine",
        "Quick Check",
        "Scam Sensitivity Dial",
        "Social Privacy Manager",
        "Text Scam",
        "Video Scam",
        "VPN",
        "Wi-Fi",
    ],
    "My Account": ["Installer/downloader", "Login", "Product UI"],
    "Password Manager": ["Login", "Policy Management", "Product UI"],
    "PC Optimizer": ["Alerting", "File Boost", "Installer/downloader", "Network Boost", "Product UI"],
    "Security Scan Plus": ["Anti-Tracker", "Product UI"],
    "SHP/MHP": ["Installer/downloader", "Login", "Product UI", "Wi-Fi"],
    "Web Advisor": [
        "Alerting",
        "Installer/downloader",
        "Login",
        "Product UI",
        "Secure Search",
        "Social Media Protection",
        "Video Scam",
    ],
    WPS: [
        "Alerting",
        "Anti-Tracker",
        "Antivirus",
        "Email Scam",
        "Firewall",
        "Identity Monitoring",
        "Installer/downloader",
        "Login",
        "Online Account Cleanup",
        "Personal Data Cleanup",
        "Product UI",
        "Quarantine",
        "Quick Check",
        "Scam Sensitivity Dial",
        "Shredder",
        "Tracker Remover",
        "Video Scam",
        "VPN",
        "Wi-Fi",
    ],
    WSS: [
        "Antivirus",
        "App Boost",
        "Disk Encryption",
        "Firewall",
        "Installer/downloader",
        "Online Account Cleanup",
        "Product UI",
        "Quarantine",
        "Shredder",
        "Tracker Remover",
        "VPN",
        "Web Boost",
        "Wi-Fi",
    ],
};

export const intents = [
    "Installation and setup",
    "Account/Subscription management",
    "Threat/Breach/Scam",
    "Uninstall and removal",
    "VPN help",
    "General device support",
] as const;

export type Intent = (typeof intents)[number];

export const assistMessageSuggestions: Record<Intent, string> = {
    "Installation and setup":
        "Looks like they’re calling about **installation and setup**. Use the guide to help with the process, or ask me here!",
    "Account/Subscription management":
        "Looks like they’re calling about **account/subscription management**. Ask me about different subscription options, how to make changes, or anything else related to McAfee!",
    "Threat/Breach/Scam":
        "Looks like they’re calling about a **security issue**. Ask me for safety tips, steps for compromised data, or anything else related to McAfee!",
    "Uninstall and removal":
        "Looks like they’re calling about **uninstalling and removal**. Ask me how to uninstall software from their device or about anything else related to McAfee!",
    "VPN help":
        "Looks like they need **VPN help**. Ask me how to set it up, troubleshoot issues, or anything else related to McAfee!",
    "General device support": "Looks like they’re calling for **general support**. Ask me if you have any questions!",
};

export type SearchType = "phoneNumber" | "billingAccountNumberAndPin";
type PhoneNumberCriteria = {
    searchType: SearchType;
    criteria: {
        phoneNumber: string;
    };
};
type BillingAccountNumberAndPinCriteria = {
    searchType: SearchType;
    criteria: {
        billingAccountNumber: string;
        customerVerificationMethod: "BANV" | "PIN";
        pin?: string;
    };
};

export type CustomerDetailsV2RequestBody = {
    partner: Partner;
} & (PhoneNumberCriteria | BillingAccountNumberAndPinCriteria);

export type CustomerDetailsV3RequestBody = { hashedMdn?: string } & CustomerDetailsV2RequestBody;

interface BaseCustomerDetailsParams {
    searchType: SearchType;
    partner: Partner;
    dispatcher?: AbstractAnalyticsDispatcher;
    customerDetailsCallback?: (result: CustomerDetailsPayload) => void;
}

export interface PhoneNumberGetCustomerDetailsParams extends BaseCustomerDetailsParams {
    searchType: "phoneNumber";
    mdn: string;
}

export interface BillingAccountNumberGetCustomerDetailsParams extends BaseCustomerDetailsParams {
    searchType: "billingAccountNumberAndPin";
    billingAccountNumber: string;
    pin?: string;
}

export type GetCustomerDetailsV1Params = PhoneNumberGetCustomerDetailsParams;

export type GetCustomerDetailsV2Params =
    | PhoneNumberGetCustomerDetailsParams
    | BillingAccountNumberGetCustomerDetailsParams;

export type GetCustomerDetailsV3Params = { modelResultsMdn?: string } & GetCustomerDetailsV2Params;
